/**
 * Incident Editor Dialog - Identification & Content Tab (PROJ-007)
 * incidentNumber (read-only), title, description, externalRef
 */

import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type { UseFormReturn } from 'react-hook-form';
import type { IncidentFormData } from './types';

interface IdentificationTabProps {
  form: UseFormReturn<IncidentFormData>;
  isEditMode: boolean;
  isViewMode?: boolean;
}

export function IdentificationTab({ form, isEditMode, isViewMode = false }: IdentificationTabProps) {
  return (
    <div className="space-y-4">
      {/* Incident Number - Read-only, auto-generated (PRE-XXX format) */}
      {isEditMode && form.getValues('incidentNumber') && (
        <FormField
          control={form.control}
          name="incidentNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Número de Incidente</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  disabled
                  className="bg-muted font-mono text-sm"
                />
              </FormControl>
              <FormDescription>
                Código único autogenerado (formato: INC-XXXXXX)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <FormField
        control={form.control}
        name="title"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Título *</FormLabel>
            <FormControl>
              <Input
                {...field}
                disabled={isViewMode}
                placeholder="Breve resumen del problema (ej: 'Error 500 en login de usuarios')"
                maxLength={255}
              />
            </FormControl>
            <FormDescription>
              <strong>Identificador breve:</strong> Una línea que resuma el problema (obligatorio, máx 255 caracteres)
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Descripción Detallada</FormLabel>
            <FormControl>
              <Textarea
                {...field}
                disabled={isViewMode}
                placeholder="¿Qué pasó? ¿Cuándo se detectó? ¿Qué usuarios/sistemas están afectados? ¿Cuál es el impacto en el negocio?"
                rows={6}
                className="resize-none"
              />
            </FormControl>
            <FormDescription>
              <strong>Narrativa del problema:</strong> Contexto completo, síntomas, impacto y situación inicial (antes de resolver)
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="externalRef"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Referencia Externa</FormLabel>
            <FormControl>
              <Input
                {...field}
                disabled={isViewMode}
                placeholder="Ej: TICKET-12345, CASE-789"
              />
            </FormControl>
            <FormDescription>
              Número de ticket/caso externo (opcional)
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="sources"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Fuentes</FormLabel>
            <FormControl>
              <Input
                {...field}
                disabled={isViewMode}
                placeholder="Ej: Email del usuario, Log del sistema, Alerta de seguridad"
              />
            </FormControl>
            <FormDescription>
              Fuentes de información del incidente (separadas por comas)
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
